import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../../helpers';
import { INTAKE_QUESTIONNAIRES_API_BASE } from '../../constants';

import type {
  IClientSibling,
  IFamily,
  IFamilyMember,
  IFamilyValueItem,
} from '../../../../../pages/common';
import type {
  CreateIntakeClientSiblingPayload,
  CreateIntakeFamilyAdditionalInfoPayload,
  CreateIntakeFamilyMemberPayload,
  DeleteIntakeClientSiblingPayload,
  DeleteIntakeFamilyAdditionalInfoPayload,
  DeleteIntakeFamilyMemberPayload,
  UpdateIntakeClientSiblingPayload,
  UpdateIntakeFamilyMemberPayload,
  UpdateIntakeFamilyPayload,
} from './types';

export function createIntakeFamilyThunks() {
  return {
    updateIntakeFamily: updateIntakeFamily(),
    createIntakeFamilyMember: createIntakeFamilyMember(),
    updateIntakeFamilyMember: updateIntakeFamilyMember(),
    deleteIntakeFamilyMember: deleteIntakeFamilyMember(),
    createIntakeClientSibling: createIntakeClientSibling(),
    updateIntakeClientSibling: updateIntakeClientSibling(),
    deleteIntakeClientSibling: deleteIntakeClientSibling(),
    createIntakeFamilyAdditionalInfo: createIntakeFamilyAdditionalInfo(),
    deleteIntakeFamilyAdditionalInfo: deleteIntakeFamilyAdditionalInfo(),
  };
}

function updateIntakeFamily() {
  return createAsyncThunk<IFamily, UpdateIntakeFamilyPayload>(
    'intakeQuestionnaire/updateIntakeFamily',
    async ({ intakeQuestionnaireId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/family`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function createIntakeFamilyMember() {
  return createAsyncThunk<IFamilyMember, CreateIntakeFamilyMemberPayload>(
    'intakeQuestionnaire/createIntakeFamilyMember',
    async ({ intakeQuestionnaireId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/family/member`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function updateIntakeFamilyMember() {
  return createAsyncThunk<IFamilyMember, UpdateIntakeFamilyMemberPayload>(
    'intakeQuestionnaire/updateIntakeFamilyMember',
    async ({ intakeQuestionnaireId, memberId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/family/member/${memberId}`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteIntakeFamilyMember() {
  return createAsyncThunk<void, DeleteIntakeFamilyMemberPayload>(
    'intakeQuestionnaire/deleteIntakeFamilyMember',
    async ({ intakeQuestionnaireId, memberId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/family/member/${memberId}`,
        getAccessTokenSilently,
      });
    },
  );
}

function createIntakeClientSibling() {
  return createAsyncThunk<IClientSibling, CreateIntakeClientSiblingPayload>(
    'intakeQuestionnaire/createIntakeClientSibling',
    async ({ intakeQuestionnaireId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/family/client_sibling`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function updateIntakeClientSibling() {
  return createAsyncThunk<IClientSibling, UpdateIntakeClientSiblingPayload>(
    'intakeQuestionnaire/updateIntakeClientSibling',
    async ({ intakeQuestionnaireId, siblingId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/family/client_sibling/${siblingId}`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteIntakeClientSibling() {
  return createAsyncThunk<void, DeleteIntakeClientSiblingPayload>(
    'intakeQuestionnaire/deleteIntakeClientSibling',
    async ({ intakeQuestionnaireId, siblingId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/family/client_sibling/${siblingId}`,
        getAccessTokenSilently,
      });
    },
  );
}

function createIntakeFamilyAdditionalInfo() {
  return createAsyncThunk<IFamilyValueItem, CreateIntakeFamilyAdditionalInfoPayload>(
    'intakeQuestionnaire/createIntakeFamilyAdditionalInfo',
    async ({ intakeQuestionnaireId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/family/additional_info`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteIntakeFamilyAdditionalInfo() {
  return createAsyncThunk<void, DeleteIntakeFamilyAdditionalInfoPayload>(
    'intakeQuestionnaire/deleteIntakeFamilyAdditionalInfo',
    async ({ intakeQuestionnaireId, itemId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/family/additional_info/${itemId}`,
        getAccessTokenSilently,
      });
    },
  );
}
